/**
 * T114-F5-g: Batch Update Dialog
 * Allows updating category, type, tags, emoji, notes, tax deductible for selected transactions.
 * Mandatory preview (dryRun) before applying.
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Eye, Pencil, ArrowRight } from 'lucide-react';
import { useAppDispatch } from '@/features/hooks';
import { batchUpdateTransactions } from '@/features/transactions/transactionsSlice';
import { useToast } from '@/hooks/use-toast';
import type { Category, BatchOperationResult } from '@/lib/types';

interface BatchUpdateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  categories: Category[];
  onSuccess: () => void;
}

export function BatchUpdateDialog({
  open,
  onOpenChange,
  selectedIds,
  categories,
  onSuccess,
}: BatchUpdateDialogProps) {
  const dispatch = useAppDispatch();
  const { toast } = useToast();

  const [categoryId, setCategoryId] = useState<string>('');
  const [type, setType] = useState<string>('');
  const [tags, setTags] = useState('');
  const [emoji, setEmoji] = useState('');
  const [notes, setNotes] = useState('');
  const [isTaxDeductible, setIsTaxDeductible] = useState(false);
  const [taxDeductibleEnabled, setTaxDeductibleEnabled] = useState(false);

  const [preview, setPreview] = useState<BatchOperationResult | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const buildUpdates = () => {
    const updates: Record<string, unknown> = {};
    if (categoryId && categoryId !== '__none__') updates.category = categoryId;
    if (type) updates.type = type;
    if (tags.trim()) updates.tags = tags.split(',').map((t) => t.trim()).filter(Boolean);
    if (emoji.trim()) updates.emoji = emoji.trim();
    if (notes.trim()) updates.notes = notes.trim();
    if (taxDeductibleEnabled) updates.isTaxDeductible = isTaxDeductible;
    return updates;
  };

  const hasUpdates = () => {
    return (
      (categoryId && categoryId !== '__none__') ||
      type ||
      tags.trim() ||
      emoji.trim() ||
      notes.trim() ||
      taxDeductibleEnabled
    );
  };

  const handlePreview = async () => {
    if (!hasUpdates()) return;
    setIsPreviewLoading(true);
    try {
      const result = await dispatch(
        batchUpdateTransactions({
          transactionIds: selectedIds,
          updates: buildUpdates() as BatchOperationResult['results'] extends undefined ? never : Record<string, unknown>,
          dryRun: true,
        })
      ).unwrap();
      setPreview(result);
    } catch (err) {
      toast({ title: 'Preview failed', description: String(err), variant: 'destructive' });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleApply = async () => {
    if (!hasUpdates()) return;
    setIsApplying(true);
    try {
      const result = await dispatch(
        batchUpdateTransactions({
          transactionIds: selectedIds,
          updates: buildUpdates() as BatchOperationResult['results'] extends undefined ? never : Record<string, unknown>,
          dryRun: false,
        })
      ).unwrap();
      toast({
        title: 'Batch update complete',
        description: `${result.affected} of ${result.total} transactions updated`,
      });
      onSuccess();
      handleClose();
    } catch (err) {
      toast({ title: 'Batch update failed', description: String(err), variant: 'destructive' });
    } finally {
      setIsApplying(false);
    }
  };

  const handleClose = () => {
    setPreview(null);
    setCategoryId('');
    setType('');
    setTags('');
    setEmoji('');
    setNotes('');
    setIsTaxDeductible(false);
    setTaxDeductibleEnabled(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="batch-update-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5" />
            Update {selectedIds.length} Transaction{selectedIds.length !== 1 ? 's' : ''}
          </DialogTitle>
          <DialogDescription>
            Set fields below. Only non-empty fields will be updated.
          </DialogDescription>
        </DialogHeader>

        {!preview ? (
          <div className="space-y-4 py-2">
            {/* Category */}
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger data-testid="batch-update-category">
                  <SelectValue placeholder="Leave unchanged" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Leave unchanged</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.emoji ? `${cat.emoji} ` : ''}{cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Type */}
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="batch-update-type">
                  <SelectValue placeholder="Leave unchanged" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tags */}
            <div className="space-y-1.5">
              <Label>Tags (comma-separated)</Label>
              <Input
                placeholder="tag1, tag2..."
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                data-testid="batch-update-tags"
              />
            </div>

            {/* Emoji */}
            <div className="space-y-1.5">
              <Label>Emoji</Label>
              <Input
                placeholder="🏷️"
                value={emoji}
                onChange={(e) => setEmoji(e.target.value)}
                maxLength={4}
                className="w-20"
                data-testid="batch-update-emoji"
              />
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input
                placeholder="Add a note..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="batch-update-notes"
              />
            </div>

            {/* Tax Deductible */}
            <div className="flex items-center gap-3">
              <Checkbox
                id="batch-tax-enable"
                checked={taxDeductibleEnabled}
                onCheckedChange={(v) => setTaxDeductibleEnabled(!!v)}
              />
              <Label htmlFor="batch-tax-enable">Set tax deductible</Label>
              {taxDeductibleEnabled && (
                <Select
                  value={isTaxDeductible ? 'yes' : 'no'}
                  onValueChange={(v) => setIsTaxDeductible(v === 'yes')}
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        ) : (
          /* Preview results */
          <div className="space-y-3 py-2" data-testid="batch-update-preview">
            <p className="text-sm font-medium">
              Preview: {preview.affected} transaction{preview.affected !== 1 ? 's' : ''} will be updated
            </p>
            {isPreviewLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <div className="max-h-[300px] overflow-y-auto space-y-2">
                {preview.results?.slice(0, 50).map((r) => (
                  <div key={r.transactionId} className="p-2 rounded border text-sm">
                    <p className="font-medium break-words">{r.description}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-muted-foreground">
                      {r.before && r.after && Object.keys(r.after).map((key) => (
                        <span key={key} className="inline-flex items-center gap-1">
                          <Badge variant="outline" className="text-[10px]">{key}</Badge>
                          <span>{String((r.before as Record<string, unknown>)?.[key] ?? '—')}</span>
                          <ArrowRight className="h-3 w-3" />
                          <span className="font-medium">{String((r.after as Record<string, unknown>)?.[key] ?? '—')}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          {!preview ? (
            <Button onClick={handlePreview} disabled={!hasUpdates() || isPreviewLoading}>
              <Eye className="mr-2 h-4 w-4" />
              {isPreviewLoading ? 'Loading...' : 'Preview Changes'}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setPreview(null)}>
                Back to Edit
              </Button>
              <Button onClick={handleApply} disabled={isApplying}>
                {isApplying ? 'Applying...' : `Apply to ${preview.affected} transactions`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
